/**
 * Tiny WebAudio synth for UI cues. The forge, the Offering and the in-run loot toasts dispatch
 * `keystone:sfx` window events ({ detail: { id } }); `installSfx` (meta/metaBridge.ts) listens
 * and plays a short synthesized sound per cue id. No samples, no assets: a few sine/triangle
 * partials with fast envelopes, mixed quietly (UI cues sit under combat).
 *
 * Off switch: localStorage `keystone.sfx` = 'off' (or `setSfxEnabled(false)`). The context is
 * created lazily on the first cue after a user gesture (browsers block audio before one).
 */

import { installAudio, playUiCue } from '../../audio/ui'; // +audio: shared mixer (UI bus), sampled layers

export const SFX_EVENT = 'keystone:sfx';
const STORAGE_KEY = 'keystone.sfx';
/** Master level (0..1). Cues are designed around this. */
const MASTER = 0.16;
/** Same cue id within this window plays once (a burst of pickups is one chime + sparkle). */
const DEBOUNCE_MS = 55;

/** Dispatch a cue (no-op outside the browser). */
export function emitSfx(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(SFX_EVENT, { detail: { id } }));
  } catch {
    /* no CustomEvent: ignore */
  }
}

interface Note {
  /** Frequency (Hz) and start offset (s). */
  f: number;
  t?: number;
  /** Length (s), peak gain (relative), waveform, pitch glide target (Hz). */
  d?: number;
  g?: number;
  w?: OscillatorType;
  to?: number;
}

/** Pentatonic-ish palette (A major-ish, bright but soft). */
const A4 = 440;
const st = (n: number): number => A4 * Math.pow(2, n / 12);

/** Cue id → notes. Prefix matches ("sfx.reveal.epic" falls back to "sfx.reveal."). */
const CUES: Readonly<Record<string, readonly Note[]>> = {
  // In-run loot (hud/loot): a soft chime that brightens with rarity.
  'sfx.loot.common': [{ f: st(7), d: 0.12, g: 0.5, w: 'sine' }, { f: st(14), t: 0.03, d: 0.1, g: 0.25 }],
  'sfx.loot.uncommon': [{ f: st(9), d: 0.14, g: 0.55 }, { f: st(16), t: 0.05, d: 0.14, g: 0.35 }],
  'sfx.loot.rare': [{ f: st(12), d: 0.18, g: 0.55 }, { f: st(16), t: 0.06, d: 0.18, g: 0.45 }, { f: st(19), t: 0.12, d: 0.22, g: 0.35 }],
  'sfx.loot.epic': [{ f: st(12), d: 0.22, g: 0.55, w: 'triangle' }, { f: st(16), t: 0.07, d: 0.22, g: 0.5 }, { f: st(19), t: 0.14, d: 0.26, g: 0.45 }, { f: st(24), t: 0.21, d: 0.4, g: 0.35 }],
  'sfx.loot.legendary': [
    { f: st(0), d: 0.5, g: 0.35, w: 'triangle' },
    { f: st(12), t: 0.02, d: 0.25, g: 0.5 },
    { f: st(16), t: 0.1, d: 0.25, g: 0.5 },
    { f: st(19), t: 0.18, d: 0.3, g: 0.5 },
    { f: st(24), t: 0.26, d: 0.6, g: 0.45 },
  ],
  'sfx.loot.discover': [{ f: st(4), d: 0.25, g: 0.35, w: 'triangle', to: st(11) }, { f: st(16), t: 0.15, d: 0.3, g: 0.3 }],
  // Altar Offering (end screen).
  'sfx.offering.line': [{ f: st(5), d: 0.12, g: 0.5 }, { f: st(12), t: 0.04, d: 0.12, g: 0.3 }],
  'sfx.offering.world': [{ f: st(7), d: 0.1, g: 0.45 }, { f: st(14), t: 0.05, d: 0.12, g: 0.35 }, { f: st(19), t: 0.1, d: 0.16, g: 0.3 }],
  'sfx.offering.sigil': [{ f: st(9), d: 0.3, g: 0.5, w: 'triangle' }, { f: st(16), t: 0.08, d: 0.3, g: 0.45 }, { f: st(21), t: 0.16, d: 0.45, g: 0.4 }],
  // Forge craft reveal.
  'sfx.forge.tap': [{ f: st(-12), d: 0.08, g: 0.6, w: 'triangle', to: st(-17) }],
  'sfx.forge.build': [{ f: st(-5), d: 0.6, g: 0.25, w: 'sine', to: st(7) }],
  'sfx.forge.build_long': [{ f: st(-5), d: 1.1, g: 0.25, w: 'sine', to: st(12) }],
  'sfx.forge.nearmiss': [{ f: st(7), d: 0.18, g: 0.4, w: 'triangle', to: st(5) }, { f: st(3), t: 0.12, d: 0.25, g: 0.3 }],
  'sfx.forge.sigil': [{ f: st(9), d: 0.25, g: 0.45 }, { f: st(14), t: 0.08, d: 0.3, g: 0.4 }],
  'sfx.forge.eye': [{ f: st(-3), d: 0.6, g: 0.3, w: 'triangle', to: st(9) }],
  'sfx.forge.': [{ f: st(0), d: 0.1, g: 0.35 }],
  'sfx.reveal.common': [{ f: st(0), d: 0.15, g: 0.4 }],
  'sfx.reveal.uncommon': [{ f: st(4), d: 0.18, g: 0.45 }, { f: st(11), t: 0.05, d: 0.18, g: 0.3 }],
  'sfx.reveal.rare': [{ f: st(7), d: 0.22, g: 0.5 }, { f: st(12), t: 0.06, d: 0.25, g: 0.4 }],
  'sfx.reveal.epic': [{ f: st(0), d: 0.6, g: 0.3, w: 'triangle' }, { f: st(12), t: 0.02, d: 0.3, g: 0.5 }, { f: st(16), t: 0.1, d: 0.35, g: 0.45 }, { f: st(19), t: 0.18, d: 0.5, g: 0.4 }],
  'sfx.reveal.legendary': [{ f: st(-12), d: 0.9, g: 0.35, w: 'triangle' }, { f: st(12), t: 0.05, d: 0.3, g: 0.5 }, { f: st(16), t: 0.15, d: 0.3, g: 0.5 }, { f: st(19), t: 0.25, d: 0.4, g: 0.5 }, { f: st(24), t: 0.35, d: 0.8, g: 0.45 }],
  'sfx.reveal.': [{ f: st(5), d: 0.2, g: 0.45 }],
  // Temper.
  'sfx.temper.start': [{ f: st(-7), d: 0.4, g: 0.25, w: 'sine', to: st(0) }],
  'sfx.temper.success': [{ f: st(7), d: 0.18, g: 0.5 }, { f: st(14), t: 0.07, d: 0.3, g: 0.45 }],
  'sfx.temper.surge': [{ f: st(7), d: 0.18, g: 0.5 }, { f: st(14), t: 0.06, d: 0.2, g: 0.5 }, { f: st(19), t: 0.12, d: 0.25, g: 0.45 }, { f: st(26), t: 0.18, d: 0.45, g: 0.4 }],
  'sfx.temper.fail_soft': [{ f: st(0), d: 0.2, g: 0.35, w: 'triangle', to: st(-5) }],
  'sfx.temper.nearmiss': [{ f: st(5), d: 0.16, g: 0.4, w: 'triangle', to: st(3) }, { f: st(10), t: 0.1, d: 0.22, g: 0.3 }],
  'sfx.awaken.': [{ f: st(0), d: 0.7, g: 0.3, w: 'triangle' }, { f: st(7), t: 0.1, d: 0.4, g: 0.45 }, { f: st(16), t: 0.22, d: 0.6, g: 0.4 }],
  // Tier sets.
  'sfx.set.piece': [{ f: st(9), d: 0.2, g: 0.45 }, { f: st(16), t: 0.08, d: 0.3, g: 0.4 }],
  'sfx.set.bonus': [{ f: st(4), d: 0.3, g: 0.4, w: 'triangle' }, { f: st(11), t: 0.1, d: 0.3, g: 0.45 }, { f: st(16), t: 0.2, d: 0.5, g: 0.4 }],
};

/** The notes a cue id plays: exact match, else the longest matching prefix key ending in '.'. */
export function cueNotes(id: string): readonly Note[] | null {
  const exact = CUES[id];
  if (exact) return exact;
  let best: string | null = null;
  for (const k of Object.keys(CUES)) if (k.endsWith('.') && id.startsWith(k) && (!best || k.length > best.length)) best = k;
  return best ? CUES[best]! : null;
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let installed = false;
let gestured = false;
const lastAt = new Map<string, number>();

export function sfxEnabled(): boolean {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setSfxEnabled(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    /* storage blocked: session only */
  }
}

function audio(): AudioContext | null {
  if (!gestured) return null;
  if (ctx) return ctx;
  const AC = (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = MASTER;
    // A gentle low-pass keeps the sine partials from feeling sharp on cheap speakers.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 5200;
    master.connect(lp).connect(ctx.destination);
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Play a cue now (respects the off switch and the debounce). */
export function playSfx(id: string): void {
  if (!sfxEnabled()) return;
  if (playUiCue(id, cueNotes(id))) return; // +audio: handled by audio/ui.ts (samples + these notes)
  const notes = cueNotes(id);
  if (!notes) return;
  const ac = audio();
  if (!ac || !master) return;
  const nowMs = performance.now();
  if (nowMs - (lastAt.get(id) ?? -Infinity) < DEBOUNCE_MS) return;
  lastAt.set(id, nowMs);
  if (ac.state === 'suspended') void ac.resume().catch(() => undefined);
  const t0 = ac.currentTime + 0.005;
  for (const n of notes) {
    const start = t0 + (n.t ?? 0);
    const dur = n.d ?? 0.15;
    const osc = ac.createOscillator();
    osc.type = n.w ?? 'sine';
    osc.frequency.setValueAtTime(n.f, start);
    if (n.to) osc.frequency.exponentialRampToValueAtTime(n.to, start + dur);
    const g = ac.createGain();
    const peak = Math.max(0.0001, n.g ?? 0.4);
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(peak, start + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(g).connect(master);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  }
}

/** Listen for `keystone:sfx` events (once). */
export function installSfx(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  installAudio(); // +audio: app-wide sound setup (UI clicks, hub ambience, mixer)
  const gesture = (): void => {
    gestured = true;
    if (ctx?.state === 'suspended') void ctx.resume().catch(() => undefined);
  };
  window.addEventListener('pointerdown', gesture, { capture: true, passive: true });
  window.addEventListener('keydown', gesture, { capture: true, passive: true });
  window.addEventListener(SFX_EVENT, (e) => {
    const id = (e as CustomEvent<{ id?: unknown }>).detail?.id;
    if (typeof id === 'string') playSfx(id);
  });
}
